import InteractionSelect from "../interaction/InteractionSelect";
import InteractionMove from "../interaction/InteractionMove";
import InteractionScale from "../interaction/InteractionScale";
import InteractionRotate from "../interaction/InteractionRotate";
import InteractionRemove from "../interaction/InteractionRemove";
import DrawingTypePicker from "./DrawingTypePicker";
import EditRemove from "../edit/EditRemove";
import PopupInput3D from "../popup/PopupInput3D";
import DitapMask from "../popup/DitapMask";
import Utils from "./Utils";

/**
 * 편집 도구 모음 위젯. DitapViewer 내부적으로 생성되어 사용하는 위젯.</br>
 * A widget that collects editing tools. The widget used internally by DitapViewer.
 *
 * @alias EditToolPicker
 * @constructor
 *
 * @param {DitapViewer} viewer 사용할 Ditap Viewer 인스턴스.</br> The DitapViewer instance to use.
 * @param {object[]} editObjArray 표시 할 편집 도구 배열.</br> Array containing editing tools to display.
 *
 * @example
 * // 1. DitapViewer 생성 시 EditToolPicker 생성 가능.
 * // editingTools에 사용할 편집 도구 작성. 작성된 편집 도구들을 담은 편집 도구 모음 위젯 생성.
 * // 활성화 - 전체 활성화(editingTools: true) 또는 부분 활성화(활성화 시킬 기능만 true)
 * // 비활성화 - false 또는 전달 안함
 * const viewer = new Ditap.DitapViewer("container", {
 *    editingTools: {
 *        select: true,
 *        createTools: {
 *            point: true,
 *            linestring: true,
 *            polygon: true,
 *        },
 *        remove: true,
 *        move: true,
 *        rotate: true,
 *        scale: true,
 *        upload: true,
 *    }
 * });
 *
 * // 2. 단독으로 선언하여 활성화
 * const viewer = new Ditap.DitapViewer("ditapContainer");
 * // 사용할 도형 생성 도구 모음 초기화
 * const editCreateObjArray = [
 *    new Ditap.InteractionPointEntity(),
 *    new Ditap.InteractionLinestringEntity(),
 *    new Ditap.InteractionPolygonEntity(),
 * ];
 *
 * // 생성 도구 모음 생성 (도구 생성 기능 사용할 경우 필수)
 * const drawingTypePicker = new Ditap.DrawingTypePicker(
 *    viewer,
 *    editCreateObjArray
 * );
 *
 * // 사용할 편집 도구 모음 초기화
 * const editObjArray = [
 *    drawingTypePicker,
 *    new Ditap.InteractionSelect(viewer),
 *    new Ditap.InteractionMove(viewer),
 *    new Ditap.InteractionScale(viewer),
 *    new Ditap.InteractionRotate(viewer),
 *    new Ditap.InteractionRemove(),
 *    new Ditap.PopupInput3D(viewer, new Ditap.Interaction3DPosition())
 * ];
 *
 * // 편집 도구 모음 생성
 * const editToolPicker = new Ditap.EditToolPicker(viewer, editObjArray);
 */

class EditToolPicker {
  constructor(viewer, editObjArray) {
    const container = viewer.toolPickerContainer;
    const mobileContainer = viewer.mobileBtnContainer;
    const that = this;

    this.isMobile = Utils.isBodyWidthLessThan600();

    // editObjArray 갯수에 따라 UI Class 설정
    let toolbarClass;
    if (editObjArray) {
      if (editObjArray.length === 1) {
        toolbarClass = "width-one";
      } else if (editObjArray.length === 2) {
        toolbarClass = "width-two";
      } else if (editObjArray.length >= 3) {
        toolbarClass = "width-three";
      }

      const li = document.createElement("li");
      const button = document.createElement("button");
      const p = document.createElement("p");
      const p2 = document.createElement("p");
      button.classList.add("ditap-edit-btn");
      p.classList.add("ditap-function-btn-img");
      p.classList.add("js-ditap-function-btn-img");
      p2.classList.add("ditap-btn-tooltip");
      p2.textContent = "편집";
      button.appendChild(p);
      button.appendChild(p2);
      li.appendChild(button);

      if (this.isMobile) {
        button.classList.add("ditap-mobile-btn");
        li.classList.add("ditap-func-btn-item");
        // mobileContainer.append(li);
        mobileContainer.querySelector(".js-position-edit").appendChild(li);
      } else {
        button.classList.add("ditap-toolbar-btn");
        li.classList.add("ditap-toolbar-right-item");
        container.appendChild(li);
      }

      const toolPopup = document.createElement("div");
      toolPopup.classList.add("mobile-tool-popup");

      const header = document.createElement("div");
      header.classList.add("tool-popup-header");

      const title = document.createElement("h2");
      title.classList.add("ditap-popup-title", "ditap-mobile-title");
      title.innerText = "편집";

      const btnWrap = document.createElement("div");
      btnWrap.classList.add("ditap-popup-btn-wrap");

      const closeBtn = document.createElement("button");
      closeBtn.classList.add("ditap-popup-close-btn");
      btnWrap.appendChild(closeBtn);
      header.appendChild(title);
      header.appendChild(closeBtn);

      toolPopup.appendChild(header);
      viewer.container.querySelector(".cesium-viewer").append(toolPopup);

      const funcList = document.createElement("div");
      funcList.classList.add("tool-popup-content");

      const ul = document.createElement("ul");
      ul.classList.add(toolbarClass);

      for (const obj of editObjArray) {
        let text;
        let type;
        let event;

        const li2 = document.createElement("li");
        const button = document.createElement("button");
        const p1 = document.createElement("p");
        const p2 = document.createElement("p");

        if (obj instanceof InteractionSelect) {
          text = "선택";
          type = "select";
          event = (e) => {
            if (!obj.isOn) {
              const onInteraction = () => {
                obj.toggle();
              };
              if (
                (viewer.currentEvent instanceof InteractionScale ||
                  viewer.currentEvent instanceof InteractionRotate ||
                  viewer.currentEvent instanceof InteractionMove) &&
                viewer.currentEvent.selectObject
              ) {
                // 기존에 실행된 인터렉션 있을 경우 종료
                viewer.offAllInterations(onInteraction);
              } else {
                viewer.offAllInterations();
                onInteraction();
              }
            } else {
              obj.toggle();
            }
          };

          // 선택 버튼 엘리먼트
          obj.buttonElement = p1;
        } else if (obj instanceof DrawingTypePicker) {
          text = "생성";
          type = "create";
          event = () => {
            if (
              (viewer.currentEvent instanceof InteractionScale ||
                viewer.currentEvent instanceof InteractionRotate ||
                viewer.currentEvent instanceof InteractionMove) &&
              viewer.currentEvent.selectObject
            ) {
              const callback = () => {
                obj.open();
              };
              // 기존에 실행된 인터렉션 있을 경우 종료
              viewer.offAllInterations(callback);
            } else {
              viewer.offAllInterations();
              obj.open();
            }
          };
        } else if (obj instanceof InteractionRemove) {
          text = "삭제";
          type = "delete";
          event = (e) => {
            if (!obj.isOn) {
              const onInteraction = () => {
                const editRemove = new EditRemove(viewer);
                obj.editRemove = editRemove;
                obj.on();
              };
              if (
                (viewer.currentEvent instanceof InteractionScale ||
                  viewer.currentEvent instanceof InteractionRotate ||
                  viewer.currentEvent instanceof InteractionMove) &&
                viewer.currentEvent.selectObject
              ) {
                // 기존에 실행된 인터렉션 있을 경우 종료
                viewer.offAllInterations(onInteraction);
              } else {
                viewer.offAllInterations();
                onInteraction();
              }
            } else {
              obj.off();
            }
          };

          // 선택 버튼 엘리먼트
          obj.buttonElement = p1;
        } else if (obj instanceof InteractionMove) {
          text = "이동";
          type = "move";
          event = (e) => {
            if (!obj.isOn) {
              const onInteraction = () => {
                obj.on();
              };
              if (
                (viewer.currentEvent instanceof InteractionScale ||
                  viewer.currentEvent instanceof InteractionRotate) &&
                viewer.currentEvent.selectObject
              ) {
                viewer.offAllInterations(onInteraction);
              } else {
                viewer.offAllInterations();
                onInteraction();
              }
            } else if (
              viewer.currentEvent instanceof InteractionMove &&
              viewer.currentEvent.selectObject
            ) {
              obj.off(true);
            } else {
              obj.off(false);
            }
          };

          // 선택 버튼 엘리먼트
          obj.buttonElement = p1;
        } else if (obj instanceof InteractionRotate) {
          text = "회전";
          type = "rotation";
          event = (e) => {
            if (!obj.isOn) {
              const onInteraction = () => {
                obj.on();
              };
              if (
                (viewer.currentEvent instanceof InteractionScale ||
                  viewer.currentEvent instanceof InteractionMove) &&
                viewer.currentEvent.selectObject
              ) {
                viewer.offAllInterations(onInteraction);
              } else {
                viewer.offAllInterations();
                onInteraction();
              }
            } else if (
              viewer.currentEvent instanceof InteractionRotate &&
              viewer.currentEvent.selectObject
            ) {
              obj.off(true);
            } else {
              obj.off(false);
            }
          };

          // 선택 버튼 엘리먼트
          obj.buttonElement = p1;
        } else if (obj instanceof InteractionScale) {
          text = "크기 조절";
          type = "size";
          event = (e) => {
            if (!obj.isOn) {
              const onInteraction = () => {
                obj.on();
              };
              if (
                (viewer.currentEvent instanceof InteractionMove ||
                  viewer.currentEvent instanceof InteractionRotate) &&
                viewer.currentEvent.selectObject
              ) {
                viewer.offAllInterations(onInteraction);
              } else {
                viewer.offAllInterations();
                onInteraction();
              }
            } else if (
              viewer.currentEvent instanceof InteractionScale &&
              viewer.currentEvent.selectObject
            ) {
              obj.off(true);
            } else {
              obj.off(false);
            }
          };

          // 선택 버튼 엘리먼트
          obj.buttonElement = p1;
        } else if (obj instanceof PopupInput3D) {
          text = "모델 추가";
          type = "model-add";
          event = () => {
            // 팝업 오픈
            if (
              (viewer.currentEvent instanceof InteractionScale ||
                viewer.currentEvent instanceof InteractionRotate ||
                viewer.currentEvent instanceof InteractionMove) &&
              viewer.currentEvent.selectObject
            ) {
              const callback = () => {
                obj.open();
              };
              // 기존에 실행된 인터렉션 있을 경우 종료
              viewer.offAllInterations(callback);
            } else {
              viewer.offAllInterations();
              obj.open();
            }
          };
        }

        li2.classList.add("ditap-function-card-list");
        button.classList.add("ditap-function-btn");
        button.classList.add(`ditap-${type}-btn`);
        p1.classList.add("ditap-function-btn-img");
        p2.classList.add("ditap-function-btn-title");
        p2.textContent = text;
        button.appendChild(p1);
        button.appendChild(p2);
        li2.appendChild(button);
        ul.append(li2);

        li2.addEventListener(
          "click",
          (this.toolPickerEvent = (e) => {
            that.close();
            event(e);
          })
        );
      }

      if (this.isMobile) {
        funcList.appendChild(ul);
        ul.classList.add("tool-popup-list");
      } else {
        li.appendChild(ul);
        ul.classList.add(
          "ditap-card",
          "ditap-function-card",
          "js-ditap-function-card",
          "hide"
        );
      }
      toolPopup.appendChild(funcList);

      closeBtn.addEventListener(
        "click",
        (this.closeEvent = (e) => {
          that.close();
        })
      );

      button.addEventListener(
        "click",
        (this.editButtonEvent = () => {
          const callback = () => {
            viewer.clickToolbar(that);
          };
          viewer.offAllInterations(callback);
        })
      );

      this._toolPicker = li;
      this._toolList = ul;
      this._imgButton = p;
      this._mobilePopup = toolPopup;
      this._editButton = button;
      this._mobileToolList = funcList;
      this.mask = new DitapMask();
      this.createResizeEvent(viewer);
    }
  }

  /*
   * 편집 도구 목록 표시하기 위한 요소.</br>
   * Display elements for the list of edit tools.
   * @type {Element}
   */
  set toolPicker(toolPicker) {
    this._toolPicker = toolPicker;
  }
  get toolPicker() {
    return this._toolPicker;
  }

  /*
   * 편집 도구 목록 표시하기 위한 요소.</br>
   * Display elements for the list of edit tools.
   * @type {Element}
   */
  set toolList(toolList) {
    this._toolList = toolList;
  }
  get toolList() {
    return this._toolList;
  }

  /*
   * 편집 도구 비활성화/활성화 버튼 요소.</br>
   * Disable/enable button element for edit tools.
   * @type {Element}
   */
  set imgButton(imgButton) {
    this._imgButton = imgButton;
  }
  get imgButton() {
    return this._imgButton;
  }

  /**
   * 편집 도구 모음 활성화.</br>
   * Activate the collection of edit tools.
   */
  open() {
    this.toolPicker.classList.add("js-toolbar-on");
    this.imgButton.classList.add("on");

    this.mask.openMobile();
    if (this.isMobile) {
      this._mobilePopup.style.display = "block";
    } else {
      this._toolList.classList.remove("hide");
    }
  }

  /**
   * 편집 도구 모음 비활성화.</br>
   * Deactivate the collection of edit tools.
   */
  close() {
    this.toolPicker.classList.remove("js-toolbar-on");
    this.imgButton.classList.remove("on");
    this.mask.closeMobile();
    if (this.isMobile) {
      this._mobilePopup.style.display = "none";
    } else {
      this._toolList.classList.add("hide");
    }
  }

  /**
   * 인스턴스 제거. </br>
   * Destroys the instance.
   */
  destroy() {
    this.removeEvent();
    this.toolPicker = undefined;
    this.toolList = undefined;
    this.imgButton = undefined;
    this._mobilePopup = undefined;
    this._editButton = undefined;
    this._mobileToolList = undefined;
    this.mask = undefined;
    this.isMobile = undefined;
  }

  /*
   * 윈도우 크기 조절 시 이벤트 생성.</br>
   * Create an event for window resize.
   * @param {DitapViewer} viewer 사용할 Ditap Viewer 인스턴스.</br> The DitapViewer instance to use.
   */
  createResizeEvent(viewer) {
    const container = viewer.toolPickerContainer;
    const mobileContainer = viewer.mobileBtnContainer;
    const that = this;

    const resizeOriginal = () => {
      // 윈도우의 현재 너비를 확인
      const currentIsMobile = Utils.isBodyWidthLessThan600();

      if (this.isMobile !== currentIsMobile) {
        if (currentIsMobile) {
          this.isMobile = true;
          this._editButton.classList.add("ditap-mobile-btn");
          this._editButton.classList.remove("ditap-toolbar-btn");

          this._toolPicker.classList.add("ditap-func-btn-item");
          this._toolPicker.classList.remove("ditap-toolbar-right-item");
          // mobileContainer.appendChild(this._toolPicker);
          mobileContainer
            .querySelector(".js-position-edit")
            .appendChild(this._toolPicker);

          this._toolList.classList.add("tool-popup-list");
          this._toolList.classList.remove(
            "ditap-card",
            "ditap-function-card",
            "js-ditap-function-card",
            "hide"
          );
          this._mobileToolList.appendChild(this._toolList);

          if (that._imgButton.classList.contains("on")) {
            this._mobilePopup.style.display = "block";
          }
        } else {
          this.isMobile = false;
          this._editButton.style.display = "flex";
          this._editButton.classList.remove("ditap-mobile-btn");
          this._editButton.classList.add("ditap-toolbar-btn");

          this._toolPicker.classList.add("ditap-toolbar-right-item");
          this._toolPicker.classList.remove("ditap-func-btn-item");
          container.appendChild(this._toolPicker);

          this._mobilePopup.style.display = "none";
          this._toolPicker.appendChild(this._toolList);
          this._toolList.classList.add(
            "ditap-card",
            "ditap-function-card",
            "js-ditap-function-card"
          );

          if (!that._imgButton.classList.contains("on")) {
            this._toolList.classList.add("hide");
            this._toolPicker.classList.remove("js-toolbar-on");
          }
          this._toolList.classList.remove("tool-popup-list");
        }
      }
    };

    this.resizeEvent = () => {
      resizeOriginal();
      setTimeout(resizeOriginal, 500);
    };

    window.addEventListener("resize", this.resizeEvent);
  }

  /*
   * 이벤트 제거.<br>
   * Remove the event registered on the window object.
   */
  removeEvent() {
    if (this.resizeEvent) {
      window.removeEventListener("resize", this.resizeEvent);
    }
    if (this.closeEvent) {
      window.removeEventListener("click", this.closeEvent);
    }
    if (this.toolPickerEvent) {
      window.removeEventListener("click", this.toolPickerEvent);
    }

    if (this.editButtonEvent) {
      window.removeEventListener("click", this.editButtonEvent);
    }
    this.toolPickerEvent = undefined;
    this.editButtonEvent = undefined;
    this.resizeEvent = undefined;
    this.closeEvent = undefined;
  }
}

export default EditToolPicker;
